import { ResourceBaseService } from "@/core/common/resource-base/resource-base.service";
import { ExpenseSheetDto } from "./expense-sheet.dto";
import { ApiHttpClient } from "@/core/common/http/api-http-client";
import { HttpRequest } from "@/core/common/http/http-request";
import { IRequest } from "@/interfaces/IRequest";
import { plainToInstance } from "class-transformer";
import { FindManyResult } from "@/core/common/resource-base/find-many-result.dto";
import { ExpenseSheetSummaryDto } from "./expense-sheet-summary.dto";

export class ExpenseSheetsResource extends ResourceBaseService<ExpenseSheetDto, null, null> {
  protected EntityDto = ExpenseSheetDto;

  constructor(protected readonly apiHttpClient: ApiHttpClient) {
    super(apiHttpClient, "/expense-sheets");
  }

  refreshExpenseSheet(id: string): Promise<void> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/refresh",
        method: "POST",
        auth: true,
      })
    );
  }

  confirmExpenseSheet(id: string): Promise<void> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/confirm",
        method: "POST",
        auth: true,
      })
    );
  }

  reopenExpenseSheet(id: string): Promise<void> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/reopen",
        method: "POST",
        auth: true,
      })
    );
  }

  confirmAllExpenseSheets(request: any): Promise<{ confirmedCount: number }> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/confirm-all",
        method: "POST",
        auth: true,
        query: this.getQueryBuilder(request).query(),
      })
    );
  }

  generateExpenseSheets(date: Date): Promise<{ jobId: string }> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/generate",
        method: "POST",
        auth: true,
        body: { date: date.toISOString() },
      })
    );
  }

  removeExpenseFromSheet(expenseSheetId: string, expenseId: string): Promise<void> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/" + expenseSheetId + "/expenses/" + expenseId,
        method: "DELETE",
        auth: true,
      })
    );
  }

  getTotal(request: IRequest): Promise<{ total: number }> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/total",
        method: "POST",
        auth: true,
        query: this.getQueryBuilder(request).query(),
      })
    );
  }

  getMine(request: IRequest = {}): Promise<any> {
    return this.apiHttpClient
      .request<ExpenseSheetDto, FindManyResult<ExpenseSheetDto>>(
        HttpRequest.create({
          url: this.resourceUrl + "/mine",
          method: "GET",
          auth: true,
          query: this.getQueryBuilder(request).query(),
        })
      )
      .then((findManyResult: FindManyResult<ExpenseSheetDto>) => {
        findManyResult.data = plainToInstance(ExpenseSheetDto, findManyResult.data);
        return findManyResult;
      });
  }

  async generatePdf(id: string): Promise<{ jobId: string }> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/pdf",
        method: "POST",
        auth: true,
      })
    );
  }

  async exportPdf(request: IRequest): Promise<{ jobId: string }> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/export-pdf",
        method: "GET",
        auth: true,
        query: this.getQueryBuilder(request).query(),
      })
    );
  }

  async getSummary(id: string): Promise<ExpenseSheetSummaryDto> {
    return this.apiHttpClient
      .request<void, ExpenseSheetSummaryDto>(
        HttpRequest.create({
          url: this.resourceUrl + "/" + id + "/summary",
          method: "GET",
          auth: true,
        })
      )
      .then((result: ExpenseSheetSummaryDto) => {
        return plainToInstance(ExpenseSheetSummaryDto, result, { excludeExtraneousValues: true });
      });
  }
}
